import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, categoryLabel, categoryColor, verdictColor } from "@/lib/pookieApi";
import { useAuth } from "@/lib/AuthContext";
import { Loader2, FolderOpen, LogIn } from "lucide-react";
import SignInModal from "@/components/SignInModal";

export default function MyCasesPage() {
  const { signedIn, user, loading: authLoading } = useAuth();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signOpen, setSignOpen] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!signedIn) {
      setLoading(false);
      return;
    }
    setLoading(true);
    api
      .get("/auth/my-cases")
      .then((r) => setCases(r.data || []))
      .catch(() => setCases([]))
      .finally(() => setLoading(false));
  }, [signedIn, authLoading]);

  if (authLoading) {
    return (
      <div className="max-w-4xl mx-auto px-5 md:px-8 py-16 font-mono-pl">
        Checking your credentials, pookie…
      </div>
    );
  }

  if (!signedIn) {
    return (
      <div className="max-w-2xl mx-auto px-5 md:px-8 py-16 text-center" data-testid="mycases-signin-prompt">
        <div className="pl-card bg-[#FFE600] p-10">
          <FolderOpen className="mx-auto mb-4" size={42} strokeWidth={3} />
          <h1 className="font-display text-4xl tracking-tighter">Sign in to see your cases.</h1>
          <p className="font-mono-pl text-sm mt-3 text-black/80">
            Phone-based sign-in unlocks your personal court docket and keeps Pookie Pro tied to you.
          </p>
          <button
            onClick={() => setSignOpen(true)}
            className="pl-btn pl-btn-primary mt-6 mx-auto"
            data-testid="mycases-signin-cta"
          >
            <LogIn size={16} strokeWidth={3} /> Sign in with phone
          </button>
        </div>
        <SignInModal open={signOpen} onClose={() => setSignOpen(false)} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-12" data-testid="mycases-page">
      <header className="mb-8">
        <h1 className="font-display text-5xl md:text-6xl tracking-tighter">My Cases</h1>
        <p className="font-mono-pl text-sm text-black/70 mt-2">
          Signed in as <b data-testid="mycases-phone">{user?.phone}</b>
        </p>
      </header>

      {loading ? (
        <div className="pl-card-flat p-10 text-center font-mono-pl">
          <Loader2 className="inline animate-spin mr-2" size={16} /> Loading…
        </div>
      ) : cases.length === 0 ? (
        <div className="pl-card bg-[#FFAEBC] p-10 text-center" data-testid="mycases-empty">
          <h2 className="font-display text-2xl">No cases on your docket yet.</h2>
          <p className="font-mono-pl text-sm mt-2 text-black/70">
            File a case while signed in and it'll show up here.
          </p>
          <Link to="/file" className="pl-btn pl-btn-primary mt-5">File your first case</Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cases.map((c, i) => {
            const bg = ["bg-[#FFAEBC]", "bg-[#B4F8C8]", "bg-[#FFE600]", "bg-[#A0E7E5]", "bg-white"][i % 5];
            return (
              <Link
                key={c.id}
                to={`/case/${c.id}`}
                data-testid={`mycases-card-${c.id}`}
                className={`pl-card p-5 ${bg} block`}
              >
                <div className="flex items-center justify-between text-xs font-mono-pl">
                  <span>{c.case_number}</span>
                  <span className={`pl-chip bg-white ${categoryColor(c.category)}`}>
                    {categoryLabel(c.category)}
                  </span>
                </div>
                <h3 className="font-display text-lg mt-3 leading-tight line-clamp-3">{c.judgment}</h3>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-xs font-mono-pl">v. {c.defendant}</span>
                  <span className={`font-display text-sm ${verdictColor(c.verdict)}`}>{c.verdict}</span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
